import { fmtUsd } from '../lib/format';

export type SpendLayer = { layer: 'seo' | 'ai'; spent: number; budget: number };

export const LAYER_LABEL: Record<'seo' | 'ai', string> = { seo: 'Core SEO', ai: 'AI Search' };

/** Warn at 80% of a layer's budget; at 100% that layer's scheduled jobs stop (spec §12). */
export const WARN_AT = 0.8;

export function spendLevel(spent: number, budget: number): 'ok' | 'warn' | 'over' {
  if (budget <= 0) return spent > 0 ? 'over' : 'ok';
  const r = spent / budget;
  return r >= 1 ? 'over' : r >= WARN_AT ? 'warn' : 'ok';
}

/**
 * Month-to-date spend per layer against its budget (from `v_spend_month` and `brands`).
 * The fill carries severity: info, then warn at 80%, then bad at 100%. The track is a lighter
 * step of the same hue so the state reads across the whole bar.
 */
export function SpendMeter({ layers, title }: { layers: SpendLayer[]; title?: string }) {
  return (
    <div className="meters">
      {title ? <span>{title}</span> : null}
      {layers.map((l) => {
        const level = spendLevel(l.spent, l.budget);
        const pct = l.budget > 0 ? Math.round((l.spent / l.budget) * 100) : l.spent > 0 ? 100 : 0;
        const label = LAYER_LABEL[l.layer];
        return (
          <div key={l.layer} className={`meter${level === 'warn' ? ' w' : level === 'over' ? ' b' : ''}`} role="status" aria-label={`${label} spend`}>
            <div className="mh">
              <span>{label}</span>
              <span className="num">
                {fmtUsd(l.spent, { exact: l.spent < 1000 })} of {fmtUsd(l.budget)}
              </span>
            </div>
            <div
              className="track"
              role="meter"
              aria-label={`${label} budget used`}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={Math.min(pct, 100)}
              aria-valuetext={`${pct}% of the monthly budget`}
            >
              <span className="fill" style={{ width: `${Math.min(pct, 100)}%` }} />
            </div>
            {level === 'over' ? (
              <span className="note b-n">Used up. Scheduled {label} checks are paused until next month.</span>
            ) : level === 'warn' ? (
              <span className="note w-n">{pct}% used</span>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}
